import { TableSelection } from 'src/services/table-utils/selection/table-selection'
import { Component, OnInit, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { Store, select } from '@ngrx/store'
import { AccountObject } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { BehaviorSubject, Observable, combineLatest } from 'rxjs'
import { map, distinctUntilChanged, startWith } from 'rxjs/operators'
import { selectAllAccounts } from 'src/app/store/accounts'
import { loadCountries, selectAllCountries } from 'src/app/store/countries'
import { loadLocations, selectAllLocations, selectLocationEntities } from 'src/app/store/locations'
import { loadPricingTerms, selectPricingTermEntities } from 'src/app/store/pricing-terms'
import { loadUsers, selectAllUsers, selectUserEntities } from 'src/app/store/users'
import { compareBy } from 'src/services/table-utils/compare'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { replayForm } from 'src/shared/utils/replay-form'
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty'
import { SelectSearchComponent } from 'src/components/select-search/select-search.component'
import { ModalService } from 'src/shared/modal'
import { BuyersGroup, BuyersGroupApiService } from '../buyers-groups-item/buyers-groups.service'
import { CreateFromSharedGroupComponent } from '../create-from-shared-group/create-from-shared-group.component'

@Component({
  selector: 'tc-add-buyers-group',
  templateUrl: './add-buyers-group.component.html',
  styleUrls: ['./add-buyers-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddBuyersGroupComponent extends OnDestroyMixin implements OnInit {
  private buyersGroupService = inject(BuyersGroupApiService)
  private toaster = inject(ToasterService)
  private store = inject(Store)
  private dialogData = inject<{
    buyers: DeepReadonly<AccountObject>[];
    groups: BuyersGroup[];
    group?: BuyersGroup;
    onSaved?: (group: BuyersGroup) => void;
    onCreated?: (group: BuyersGroup) => void;
}>(MAT_DIALOG_DATA)
  private formBuilder = inject(FormBuilder)
  private dialogRef = inject<MatDialogRef<AddBuyersGroupComponent>>(MatDialogRef)
  private modal = inject(ModalService)

  formGroup: FormGroup
  allBuyers: DeepReadonly<AccountObject>[]
  groups: BuyersGroup[]
  countries$: Observable<any[]>
  locations$: Observable<any[]>
  managers$: Observable<any[]>
  filteredBuyers$: Observable<DeepReadonly<AccountObject>[]>
  addedBuyerIds$ = new BehaviorSubject<string[]>([])
  selectedBuyers$: Observable<any[]>
  selectedBuyersDataSource = new MatTableDataSource<any>([])
  selectedInSelectbox$: Observable<number>
  isEditMode = false
  selectedBuyersColumns = ['select', 'name', 'country', 'location', 'primaryAccountManager', 'incoterm', 'actions']
  selection = new TableSelection<any>('id')
  @ViewChild('buyersSelect') buyersSelect: SelectSearchComponent<any>
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) this.selectedBuyersDataSource.sort = sort
  }

  constructor() {
    super()
    this.formGroup = this.formBuilder.group({
      buyers: [null],
      name: [null, Validators.required],
      destCountry: [null],
      destLocation: [null],
      primaryAcctManager: [null],
    })
    this.allBuyers = this.dialogData.buyers
    this.groups = this.dialogData.groups

    if (this.dialogData.group) {
      this.isEditMode = true
      this.formGroup.patchValue({ name: this.dialogData.group.name })
      this.addedBuyerIds$.next([...this.dialogData.group.buyers])
    }

    this.countries$ = this.store.pipe(
      select(selectAllCountries),
      waitNotEmpty(),
      map(list => list.sort(compareBy('name')))
    )
    this.locations$ = this.store.pipe(
      select(selectAllLocations),
      waitNotEmpty(),
      map(list => list.sort(compareBy('name')))
    )
    this.managers$ = this.store.pipe(
      select(selectAllUsers),
      waitNotEmpty(),
      map(users => users
        .filter(user => !user.archived && (user.role === 'manager' || user.role === 'trader'))
        .sort(compareBy('fullname'))
      )
    )
    const baseBuyers$ = this.setupBuyerFiltering()
    this.filteredBuyers$ = combineLatest([baseBuyers$, this.addedBuyerIds$]).pipe(
      map(([buyers, addedIds]) => {
        if (!buyers) return []
        const addedSet = new Set(addedIds)
        return buyers.map(buyer => addedSet.has(String(buyer.account))
          ? { ...buyer, $hiddenOption: true }
          : buyer
        )
      }),
      startWith([])
    )
    this.selectedBuyers$ = this.setupSelectedBuyers()
    this.selectedInSelectbox$ = this.formGroup.get('buyers').valueChanges.pipe(
      startWith(null),
      map((selectedIds: string[] | null) => selectedIds?.length || 0)
    )
  }

  ngOnInit(): void {
    this.store.dispatch(loadCountries())
    this.store.dispatch(loadLocations({}))
    this.store.dispatch(loadUsers({}))
    this.store.dispatch(loadPricingTerms({}))
    this.setupFilterChangeSubscription()
    this.selectedBuyers$.pipe(
      untilComponentDestroyed(this)
    ).subscribe(buyers => {
      this.selectedBuyersDataSource.data = buyers
    })
  }

  private setupFilterChangeSubscription(): void {
    replayForm(this.formGroup).pipe(
      map(formValue => ({
        destCountry: formValue.destCountry,
        destLocation: formValue.destLocation,
        primaryAcctManager: formValue.primaryAcctManager
      })),
      distinctUntilChanged((prev, curr) =>
        JSON.stringify(prev) === JSON.stringify(curr)
      ),
      untilComponentDestroyed(this)
    ).subscribe(() => {
      this.formGroup.get('buyers').setValue(null)
    })
  }

  private setupBuyerFiltering(): Observable<DeepReadonly<AccountObject>[]> {
    return combineLatest([
      replayForm(this.formGroup),
      this.store.pipe(select(selectLocationEntities), waitNotEmpty())
    ]).pipe(
      map(([formValue, locationsByIds]) => {
        const { destCountry, destLocation, primaryAcctManager } = formValue

        if ((!destCountry || destCountry.length === 0) &&
            (!destLocation || destLocation.length === 0) &&
            (!primaryAcctManager || primaryAcctManager.length === 0)) {
          return this.allBuyers
        }

        return this.allBuyers.filter((buyer) => {
          const addressMatch = buyer.addresses?.some(addr =>
            addr.primary && destCountry?.includes(addr.cc)
          )
          const locationMatch = destCountry?.includes(locationsByIds[buyer.attributes?.default_location]?.country)
          const locationsMatch = buyer.attributes?.locations?.some(locationId =>
            destLocation?.includes(locationId)
          )
          const buyerLocationCountryMatch = buyer.attributes?.locations?.some(locationId =>
            destCountry?.includes(locationsByIds[locationId]?.country)
          )
          const managerMatch = buyer.manager && primaryAcctManager?.includes(buyer.manager)
          const managersMatch = buyer.managers?.some(managerId =>
            primaryAcctManager?.includes(managerId)
          )

          const countryMatches = !destCountry?.length || addressMatch || locationMatch || buyerLocationCountryMatch
          const locationMatches = !destLocation?.length || locationsMatch
          const managerMatches = !primaryAcctManager?.length || managerMatch || managersMatch

          return countryMatches && locationMatches && managerMatches
        })
      })
    )
  }

  private setupSelectedBuyers(): Observable<any[]> {
    return combineLatest([
      this.addedBuyerIds$,
      this.store.pipe(select(selectAllAccounts), waitNotEmpty()),
      this.store.pipe(select(selectAllCountries), waitNotEmpty()),
      this.store.pipe(select(selectLocationEntities), waitNotEmpty()),
      this.store.pipe(select(selectPricingTermEntities), waitNotEmpty()),
      this.store.pipe(select(selectUserEntities), waitNotEmpty()),
    ]).pipe(
      map(([addedBuyerIds, accounts, countries, locationsByIds, pricingTermsById, usersById]) => {
        if (!addedBuyerIds || addedBuyerIds.length === 0) return []
        const countriesByCode = new Map(countries.map(c => [c.code, c.name]))
        return addedBuyerIds.map((id: string) => {
          const buyer = accounts.find(b => String(b.account) === String(id))
          if (!buyer) return null
          const primaryAddress = buyer.addresses?.find(a => a.primary) || buyer.addresses?.[0]
          const country = primaryAddress?.cc ? countriesByCode.get(primaryAddress.cc) || primaryAddress.cc : ''
          const location = buyer.attributes?.default_location ? locationsByIds[buyer.attributes.default_location]?.name || '' : ''
          const incotermId = buyer.attributes?.pricing?.incoterm
          const incoterm = incotermId ? pricingTermsById[incotermId]?.term || '' : ''
          const primaryAccountManager = buyer.manager ? usersById[buyer.manager]?.fullname || '' : ''
          return { id, name: buyer.name, country, location, primaryAccountManager, incoterm }
        }).filter(Boolean)
      })
    )
  }

  onCheckboxClick(event: MouseEvent, row: any) {
    event.stopPropagation()
    this.selection.toggleRowWithShift(event, row, this.selectedBuyersDataSource.data)
  }

  toggleAllRows(dataSource: any[]) {
    const status = this.selection.selectAllStatus(dataSource)
    this.selection.toggleRows(status !== 'all', dataSource)
  }

  addSelectedBuyers() {
    const selectedIds: string[] = this.formGroup.get('buyers').value || []
    if (selectedIds.length === 0) return

    const currentAddedIds = this.addedBuyerIds$.value
    const newAddedIds = [...new Set([...currentAddedIds, ...selectedIds])]
    this.addedBuyerIds$.next(newAddedIds)
    this.formGroup.get('buyers').setValue(null)
    setTimeout(() => this.buyersSelect?.filterItems())
  }

  removeBuyer(buyerId: string) {
    const currentAddedIds = this.addedBuyerIds$.value
    this.addedBuyerIds$.next(currentAddedIds.filter(id => id !== buyerId))
    setTimeout(() => this.buyersSelect?.filterItems())
  }

  removeSelectedBuyers() {
    const selectedIds = new Set(this.selection.selectedIds.keys())
    const currentAddedIds = this.addedBuyerIds$.value
    this.selection.clearSelection()
    this.addedBuyerIds$.next(currentAddedIds.filter(id => !selectedIds.has(id)))
    setTimeout(() => this.buyersSelect?.filterItems())
  }

  cancel() {
    this.dialogRef.close()
  }

  async save() {
    if (!this.formGroup.get('name').valid) {
      this.toaster.error('Please provide a group name.')
      return
    }
    if (this.addedBuyerIds$.value.length === 0) {
      this.toaster.error('Please add at least one buyer to the group.')
      return
    }

    const group = {
      ...this.formGroup.value,
      buyers: this.addedBuyerIds$.value
    }
    try {
      if (this.isEditMode) {
        const sourceGroup = this.dialogData.group
        if (sourceGroup?.shared_by && !sourceGroup?.is_forked) {
          this.modal.openDialog(CreateFromSharedGroupComponent, { buyers: group.buyers })
            .subscribe((newGroup?: BuyersGroup) => {
              if (!newGroup) return
              this.dialogData.onCreated?.(newGroup)
              this.dialogRef.close(newGroup)
            })
          return
        }
        const updatePayload = { ...sourceGroup, name: group.name, buyers: group.buyers }
        const { data } = await this.buyersGroupService.update(updatePayload)
        this.toaster.success(`Buyer Group "${group.name}" was updated successfully.`)
        this.dialogRef.close(data)
        this.dialogData.onSaved?.(data)
      } else {
        const { data } = await this.buyersGroupService.create(group)
        this.toaster.success(`Buyer Group "${group.name}" was created successfully.`)
        this.groups.unshift(data)
        this.dialogRef.close(data)
      }
    } catch {
      this.toaster.error(`Unable to ${this.isEditMode ? 'update' : 'create'} Buyer Group "${group.name}".`)
    }
  }
}
